import React, { useEffect, useRef, useState } from "react";

import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import api from "../../../api";
import { Button, RenderFormFieldsHOC, SkeletonFormView } from "../../../components";
import { NewBenefitModal } from "../Components/BenefitModal";

export const ViewRule = () => {
    const [ruleDataLoading, setRuleDataLoading] = useState(false);
    const [ruleUIConfig, setRuleUIConfig] = useState(null);
    const [formSubmitClick, setFormSubmitClick] = useState(false);
    const [isFormError, setIsFormError] = useState(false);
    const [saveDraftLoading, setSaveDraftLoading] = useState(false);
    const [publishLoading, setPublishLoading] = useState(false);
    const [viewMode, setViewMode] = useState(true)
    const [submitButtonClicked, setSubmitButtonClicked] = useState(null)
    const [benefitBaseLoading, setBenefitBaseLoading] = useState(false)
    const [benefitBase, setBenefitBase] = useState(null)
    const [showNewbenefitModal, setShowNewBenefitModal] = useState(false)

    const configRef = useRef(null)
    const benefitModalRef = useRef(null)
    const navigate = useNavigate()
    const params = useParams()
    const [searchParams] = useSearchParams();
    
    useEffect(() => {
        if (ruleUIConfig) {
            //console.log("ruleUIConfig in viewRule: ", ruleUIConfig)
            configRef.current = ruleUIConfig.catalogComponent
            //in this way whenever I am changing the state I can manual trigger config to update and anyways render function call is happening
            setRuleDataLoading(false)
        }
    }, [ruleUIConfig])

    const getRuleData = async ({ ruleId }) => {
        try {
            setRuleDataLoading(true)
            const response = await api.getRuleById({
                ruleId
            })

            setRuleUIConfig(response)
            setRuleDataLoading(false)
        } catch (err) {
            setRuleDataLoading(false)
            toast.error("Unable to fetch Rule details", {
                position: "bottom-right"
            })
        }
    }

    const getBasebenefit = async ({ catalogType, catalogId, categoryType }) => {
        try {
            if (benefitModalRef && benefitModalRef.current) {
                benefitModalRef.current?.showModal()
            }
            setShowNewBenefitModal(true)
            setBenefitBaseLoading(true)
            const response = await api.getBasebenefit({
                catalogType,
                catalogId,
                categoryType
            })

            setBenefitBase(response)
            setBenefitBaseLoading(false)
        } catch (err) {
            setBenefitBaseLoading(false)
            setShowNewBenefitModal(false)
            toast.error("Unable to fetch", {
                position: "bottom-right"
            })
        }
    }

    const getSelectedRuleType = () => {
        if (configRef.current) {
            const ruleTypeConfig = configRef.current.children[0].children[0]
            const { valueConfig: { selectedValues } } = ruleTypeConfig || {};

            if (selectedValues && selectedValues.length) {
                return selectedValues[0]
            } else {
                return ""
            }
        }
        return ""
    }

    useEffect(() => {
        if (params) {
            const { ruleId } = params
            getRuleData({ ruleId })
        }
    }, [params])

    useEffect(() => {
        if (searchParams) {
            if (searchParams.get("mode") === "view") {
                setViewMode(true)
                setFormSubmitClick(false)
                setSubmitButtonClicked("")
            } else {
                setViewMode(false)
            }
        }
    }, [searchParams])

    useEffect(() => {
        //Handle form submit to api call, currently only for Drafts
        if (!isFormError && formSubmitClick) {
            let status = "DRAFT"
            if (submitButtonClicked === "DRAFT") {
                status = "DRAFT"
            }
            else if (submitButtonClicked === "PUBLISH") {
                status = "PUBLISHED"
            }
            updateRuleData({ status })
        }
    }, [formSubmitClick, isFormError, submitButtonClicked])


    const errorCallback = (error) => {
        if (error) {
            // alert(config.keyName + " has error")
            //restrict api calls
            if (!isFormError) { //avoid multiple updates
                setIsFormError(error)
            }
        }
    }

    const updateRuleData = async ({ status }) => {
        try {
            if (status === "DRAFT") {
                setSaveDraftLoading(true)
            } else if (status === "PUBLISHED") {
                setPublishLoading(true)
            }

            const response = await api.updateRule({
                dataJson: {
                    catalogComponent: configRef.current,
                    catalogInstanceDetails: {
                        ...(ruleUIConfig.catalogInstanceDetails),
                        catalogState: status
                    }
                }
            })

            if (response) {
                setSaveDraftLoading(false)
                setPublishLoading(false)

                const { catalogInstanceDetails } = response
                if (status === "DRAFT") {
                    toast.success("Saved Rule to Drafts")
                }
                else if (status === "PUBLISHED") {
                    toast.success("Rule published successfully")
                }
                if (catalogInstanceDetails?.status === "SUCCESS") {
                    navigate(`/rules/rule/${params.ruleId}?mode=view`)
                }
            }
        } catch (err) {
            setSaveDraftLoading(false)
            setPublishLoading(false)

            let errorMessage = err.response?.data?.errorMessage
            toast.error(errorMessage ? errorMessage : "Unable to save Rule")
            setFormSubmitClick(false)
        }
    }

    return (
        <div className="w-full h-full overflow-auto px-8 py-3">

            <div className="flex flex-col gap-6 pt-4 px-2">
                {ruleDataLoading && <SkeletonFormView />}

                {!ruleDataLoading && ruleUIConfig && configRef.current &&
                    <>
                        {
                            <RenderFormFieldsHOC
                                config={configRef.current}
                                parent={configRef.current}
                                childIndex={0}
                                sectionId={""}
                                formSubmitClick={formSubmitClick}
                                errorCallback={errorCallback}
                                rerenderFormUI={setRuleUIConfig}
                                viewMode={viewMode}
                            />
                        }
                    </>
                }
            </div>

            {!ruleDataLoading && ruleUIConfig &&
                <div className="flex flex-col gap-6 pt-4 px-2">
                    {
                        !viewMode &&
                        <div className="flex justify-end items-center gap-2 mt-2">
                            <Button
                                variant="primary"
                                disabled={benefitBaseLoading}
                                onClick={() => {
                                    const selectedRuleType = getSelectedRuleType()
                                    if (selectedRuleType) {
                                        getBasebenefit({
                                            catalogId: "BENEFIT_BASE",
                                            catalogType: "BENEFIT",
                                            categoryType: selectedRuleType
                                        })
                                    } else {
                                        toast.error("Please select a rule type", {
                                            position: "bottom-right"
                                        })
                                    }
                                }}
                            >
                                Add Benefits
                            </Button>
                            <Button
                                variant="primary"
                                disabled={saveDraftLoading}
                                onClick={() => {
                                    setFormSubmitClick(true)
                                    setSubmitButtonClicked("DRAFT")
                                    //post api call or any error revert back to false
                                }
                                }
                            >
                                Save to Drafts
                            </Button>
                            <Button
                                variant="primary"
                                disabled={publishLoading}
                                onClick={() => {
                                    setFormSubmitClick(true)
                                    setSubmitButtonClicked("PUBLISH")
                                    //post api call or any error revert back to false
                                }
                                }
                            >
                                Publish Rule
                            </Button>
                        </div>
                    }
                </div>
            }

            {
                showNewbenefitModal &&
                <NewBenefitModal
                    showModal={showNewbenefitModal}
                    categoryType={getSelectedRuleType()}
                    formConfigLoading={benefitBaseLoading}
                    formConfig={benefitBase}
                    closeModalCallback={(newbenefitConfig) => {
                        setShowNewBenefitModal(false)
                        if (newbenefitConfig) {
                            setRuleUIConfig(prev => ({
                                ...prev,
                                catalogComponent: {
                                    ...prev.catalogComponent,
                                    children: [
                                        ...(prev.catalogComponent.children),
                                        {
                                            ...newbenefitConfig
                                        }
                                    ]
                                }
                            }))
                        }
                    }}
                    errorCallback={errorCallback}
                    ref={benefitModalRef}
                />
            }
        </div>
    )
}
